import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const OperatingMargin = () => {
  // State variables for Operating Margin calculation
  const [operatingIncome, setOperatingIncome] = useState(0); // Operating income
  const [revenue, setRevenue] = useState(0); // Total revenue
  const [operatingMargin, setOperatingMargin] = useState(0); // Calculated operating margin

  // Event handlers to update state values
  const handleOperatingIncomeChange = (e) => setOperatingIncome(parseFloat(e.target.value) || 0);
  const handleRevenueChange = (e) => setRevenue(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate operating margin when any input changes
  useEffect(() => {
    if (operatingIncome && revenue) {
      const margin = (operatingIncome / revenue) * 100; // Calculate operating margin
      setOperatingMargin(margin);
    } else {
      setOperatingMargin(0);
    }
  }, [operatingIncome, revenue]);

  return (
    <Layout title="Operating Margin Calculator" path="operating-margin">
      <section className="section-padding" id="operating-margin-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Operating Margin Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Operating Margin Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Operating Income Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="operatingIncome" className="form-label">
                        Operating Income (₹)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="operatingIncome"
                        placeholder="Enter operating income"
                        value={operatingIncome || ""}
                        onChange={handleOperatingIncomeChange}
                      />
                    </div>

                    {/* Revenue Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="revenue" className="form-label">
                        Revenue (₹)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="revenue"
                        placeholder="Enter total revenue"
                        value={revenue || ""}
                        onChange={handleRevenueChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Operating Margin:{" "}
                        <span className="fw-bold ms-3">
                          {operatingMargin === 0 ? "0.00" : operatingMargin.toFixed(2)} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Operating Margin Calculator
                </h3>

                {/* What is an Operating Margin Calculator? */}
                <h4 className="mt-3 mb-3">What is an Operating Margin Calculator?</h4>
                <p>
                  An **Operating Margin Calculator** helps you calculate the percentage of revenue that remains after accounting for operating expenses. This metric is useful for evaluating the profitability of a company's core operations, excluding non-operating items such as taxes and interest.
                </p>

                {/* Why Use an Operating Margin Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Operating Margin Calculator?</h4>
                <p>
                  This calculator helps businesses, analysts, and investors determine how efficiently a company is generating profit from its core activities. By calculating operating margin, it is easier to compare performance across companies and industries.
                </p>

                {/* Benefits of Using an Operating Margin Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Operating Margin Calculator</h4>
                <ul>
                  <li><strong>Helps assess operational efficiency:</strong> Provides insights into the cost management and profitability of the company’s core operations.</li>
                  <li><strong>Guides management in financial decision-making:</strong> Assists in identifying areas for cost-cutting and operational improvements.</li>
                  <li><strong>Useful for comparing profitability with competitors:</strong> Helps compare your company’s operating margin with competitors to assess market position.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Operating Margin Calculator?</h4>
                <p>
                  The Operating Margin Calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Corporations:</strong> For internal financial analysis and improving operational efficiency.</li>
                  <li><strong>Financial Analysts:</strong> To assess a company’s financial health and profitability.</li>
                  <li><strong>Investors:</strong> To evaluate the profitability and management efficiency of potential investments.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What does a high operating margin indicate?</h4>
                <p>
                  A high operating margin typically indicates a company is efficiently managing its costs and is profitable from its core business activities. This is usually a sign of good management and competitive advantage.
                </p>

                <h4 className="mt-4 mb-3">How can a company improve its operating margin?</h4>
                <p>
                  A company can improve its operating margin by reducing operating costs, increasing operational efficiency, or growing revenue through more profitable products or services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default OperatingMargin;
